<template>
  <div class="px-3 pt-2 h-full overflow-auto pb-5 text-[13px] leading-normal text-dark-text">
    <!-- ─── Input Variable ─── -->
    <VariableSelect label="Input Variable" v-model="inputVariable" class="w-full mt-3" system :filter-data-type="['Excel']" @update:modelValue="onPayloadChange" />
    <p v-if="submitted && !inputVariable" class="mt-1 text-[11px] text-alert font-medium">Input variable is required.</p>

    <div class="mt-3 px-4 py-3 border border-stroke rounded-t-md">
      <!-- ─── Sheet Selection ─── -->
      <div class="mb-4">
        <label class="block text-xs font-semibold text-semi-dark-text mb-1">Sheet Name or Index</label>
        <div class="flex items-center gap-2">
          <select v-model="sheetRefType" class="px-2 py-1 text-xs border border-stroke rounded bg-white text-dark-text focus:outline-none focus:ring-1 focus:ring-secondary" @change="onPayloadChange">
            <option value="index">By Index</option>
            <option value="name">By Name</option>
          </select>
          <input
            v-if="sheetRefType === 'index'"
            v-model.number="sheetIndex"
            type="number"
            min="1"
            placeholder="1"
            class="flex-1 px-2 py-1 text-xs border border-stroke rounded bg-white text-dark-text focus:outline-none focus:ring-1 focus:ring-secondary"
            @input="onPayloadChange"
          />
          <input
            v-else
            v-model="sheetName"
            type="text"
            placeholder="Sheet1"
            class="flex-1 px-2 py-1 text-xs border border-stroke rounded bg-white text-dark-text focus:outline-none focus:ring-1 focus:ring-secondary"
            @input="onPayloadChange"
          />
        </div>
        <p class="mt-1 text-[11px] text-semi-dark-text">Select the worksheet by its 1-based index or name.</p>
      </div>

      <!-- ─── Header Configuration ─── -->
      <div class="mb-4 p-3 border border-stroke rounded bg-surface-light">
        <label class="block text-xs font-semibold text-semi-dark-text mb-2">Column Headers</label>

        <div class="flex items-center gap-2 mb-2">
          <label class="flex items-center gap-1 cursor-pointer">
            <input type="radio" value="row" v-model="headerMode" class="accent-secondary" @change="onPayloadChange" />
            <span class="text-xs text-dark-text">Headers from a row</span>
          </label>
          <label class="flex items-center gap-1 cursor-pointer">
            <input type="radio" value="none" v-model="headerMode" class="accent-secondary" @change="onPayloadChange" />
            <span class="text-xs text-dark-text">No headers in file</span>
          </label>
        </div>

        <!-- Header row index -->
        <div v-if="headerMode === 'row'" class="flex items-center gap-2 mt-2">
          <label class="text-xs text-semi-dark-text whitespace-nowrap">Header row number:</label>
          <input
            v-model.number="headerRowIndex"
            type="number"
            min="1"
            placeholder="1"
            class="w-20 px-2 py-1 text-xs border border-stroke rounded bg-white text-dark-text focus:outline-none focus:ring-1 focus:ring-secondary"
            @input="onPayloadChange"
          />
          <p class="text-[11px] text-semi-dark-text">(1-based)</p>
        </div>

        <!-- Manual header definitions -->
        <div v-if="headerMode === 'none'" class="mt-2">
          <p class="text-[11px] text-semi-dark-text mb-2">Define column headers manually. Column index is 1-based (A=1, B=2, …).</p>
          <div class="space-y-2">
            <div v-for="(header, idx) in manualHeaders" :key="idx" class="flex items-center gap-2">
              <input
                v-model="header.name"
                type="text"
                placeholder="Header Name"
                class="flex-1 px-2 py-1 text-xs border border-stroke rounded bg-white text-dark-text focus:outline-none focus:ring-1 focus:ring-secondary"
                @input="onPayloadChange"
              />
              <input
                v-model.number="header.columnIndex"
                type="number"
                min="1"
                placeholder="Col #"
                class="w-20 px-2 py-1 text-xs border border-stroke rounded bg-white text-dark-text focus:outline-none focus:ring-1 focus:ring-secondary"
                @input="onPayloadChange"
              />
              <button type="button" class="p-1 rounded text-alert hover:bg-alert hover:bg-opacity-10 transition-colors" title="Remove header" @click="removeManualHeader(idx)">
                <svg class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1
                    0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
          <button
            type="button"
            class="mt-2 px-3 py-1 text-xs font-medium text-secondary border border-secondary rounded hover:bg-secondary hover:text-on-secondary transition-colors"
            @click="addManualHeader"
          >
            + Add Header
          </button>
        </div>
      </div>

      <!-- ─── Data Rows Configuration ─── -->
      <div class="mb-4 p-3 border border-stroke rounded bg-surface-light">
        <label class="block text-xs font-semibold text-semi-dark-text mb-2">Data Rows</label>

        <div class="flex items-center gap-2 mb-2">
          <label class="flex items-center gap-1 cursor-pointer">
            <input type="radio" value="afterHeader" v-model="dataRowMode" class="accent-secondary" @change="onPayloadChange" />
            <span class="text-xs text-dark-text">Start after header row</span>
          </label>
          <label class="flex items-center gap-1 cursor-pointer">
            <input type="radio" value="custom" v-model="dataRowMode" class="accent-secondary" @change="onPayloadChange" />
            <span class="text-xs text-dark-text">Custom start row</span>
          </label>
        </div>

        <div v-if="dataRowMode === 'custom'" class="flex items-center gap-2 mt-2">
          <label class="text-xs text-semi-dark-text whitespace-nowrap">Data start row:</label>
          <input
            v-model.number="dataStartRow"
            type="number"
            min="1"
            placeholder="2"
            class="w-20 px-2 py-1 text-xs border border-stroke rounded bg-white text-dark-text focus:outline-none focus:ring-1 focus:ring-secondary"
            @input="onPayloadChange"
          />
          <p class="text-[11px] text-semi-dark-text">(1-based)</p>
        </div>

        <!-- Optional end row -->
        <div class="flex items-center gap-2 mt-3">
          <label class="flex items-center gap-1 cursor-pointer">
            <input type="checkbox" v-model="hasEndRow" class="accent-secondary" @change="onPayloadChange" />
            <span class="text-xs text-dark-text">Limit end row</span>
          </label>
          <input
            v-if="hasEndRow"
            v-model.number="dataEndRow"
            type="number"
            min="1"
            placeholder="100"
            class="w-20 px-2 py-1 text-xs border border-stroke rounded bg-white text-dark-text focus:outline-none focus:ring-1 focus:ring-secondary"
            @input="onPayloadChange"
          />
        </div>
        <p class="mt-1 text-[11px] text-semi-dark-text">Leave unchecked to read all rows until the last non-empty row.</p>
      </div>

      <!-- ─── Column Range Configuration ─── -->
      <div class="mb-4 p-3 border border-stroke rounded bg-surface-light">
        <label class="block text-xs font-semibold text-semi-dark-text mb-2">Column Range</label>

        <div class="flex items-center gap-2 mb-2">
          <label class="flex items-center gap-1 cursor-pointer">
            <input type="radio" value="all" v-model="columnMode" class="accent-secondary" @change="onPayloadChange" />
            <span class="text-xs text-dark-text">All columns</span>
          </label>
          <label class="flex items-center gap-1 cursor-pointer">
            <input type="radio" value="range" v-model="columnMode" class="accent-secondary" @change="onPayloadChange" />
            <span class="text-xs text-dark-text">Custom range</span>
          </label>
          <label class="flex items-center gap-1 cursor-pointer">
            <input type="radio" value="pick" v-model="columnMode" class="accent-secondary" @change="onPayloadChange" />
            <span class="text-xs text-dark-text">Pick columns</span>
          </label>
        </div>

        <!-- Range -->
        <div v-if="columnMode === 'range'" class="flex items-center gap-2 mt-2">
          <label class="text-xs text-semi-dark-text">From col:</label>
          <input
            v-model.number="columnStart"
            type="number"
            min="1"
            placeholder="1"
            class="w-16 px-2 py-1 text-xs border border-stroke rounded bg-white text-dark-text focus:outline-none focus:ring-1 focus:ring-secondary"
            @input="onPayloadChange"
          />
          <label class="text-xs text-semi-dark-text">To col:</label>
          <input
            v-model.number="columnEnd"
            type="number"
            min="1"
            placeholder="10"
            class="w-16 px-2 py-1 text-xs border border-stroke rounded bg-white text-dark-text focus:outline-none focus:ring-1 focus:ring-secondary"
            @input="onPayloadChange"
          />
          <p class="text-[11px] text-semi-dark-text">(1-based)</p>
        </div>

        <!-- Pick specific columns -->
        <div v-if="columnMode === 'pick'" class="mt-2">
          <p class="text-[11px] text-semi-dark-text mb-2">Enter 1-based column indices, e.g. 1, 3, 5 to pick columns A, C, E.</p>
          <div class="flex flex-wrap gap-2">
            <div v-for="(col, idx) in pickedColumns" :key="idx" class="flex items-center gap-1">
              <input
                v-model.number="pickedColumns[idx]"
                type="number"
                min="1"
                placeholder="#"
                class="w-14 px-2 py-1 text-xs border border-stroke rounded bg-white text-dark-text focus:outline-none focus:ring-1 focus:ring-secondary"
                @input="onPayloadChange"
              />
              <button type="button" class="p-1 rounded text-alert hover:bg-alert hover:bg-opacity-10 transition-colors" title="Remove" @click="removePickedColumn(idx)">
                <svg class="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1
                    0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
          <button
            type="button"
            class="mt-2 px-3 py-1 text-xs font-medium text-secondary border border-secondary rounded hover:bg-secondary hover:text-on-secondary transition-colors"
            @click="addPickedColumn"
          >
            + Add Column
          </button>
        </div>

        <p class="mt-2 text-[11px] text-semi-dark-text">"All columns" scans every column up to the last non-empty cell, including gaps.</p>
      </div>

      <!-- ─── Empty / Gap Handling ─── -->
      <div class="mb-4 p-3 border border-stroke rounded bg-surface-light">
        <label class="block text-xs font-semibold text-semi-dark-text mb-2">Gap &amp; Empty Cell Handling</label>

        <div class="flex items-center gap-2 mb-2">
          <label class="flex items-center gap-1 cursor-pointer">
            <input type="checkbox" v-model="skipEmptyRows" class="accent-secondary" @change="onPayloadChange" />
            <span class="text-xs text-dark-text">Skip entirely empty rows</span>
          </label>
        </div>

        <div class="flex items-center gap-2">
          <label class="text-xs text-semi-dark-text whitespace-nowrap">Empty cell value:</label>
          <select
            v-model="emptyCellValue"
            class="px-2 py-1 text-xs border border-stroke rounded bg-white text-dark-text focus:outline-none focus:ring-1 focus:ring-secondary"
            @change="onPayloadChange"
          >
            <option value="null">null</option>
            <option value="empty">"" (empty string)</option>
            <option value="undefined">undefined (omit key)</option>
          </select>
        </div>
      </div>

      <!-- ─── Column Rename / Alias Mapping ─── -->
      <div class="p-3 border border-stroke rounded bg-surface-light">
        <div class="flex items-center justify-between mb-2">
          <label class="text-xs font-semibold text-semi-dark-text">Column Rename / Alias (optional)</label>
          <label class="flex items-center gap-1 cursor-pointer">
            <input type="checkbox" v-model="enableAliases" class="accent-secondary" @change="onPayloadChange" />
            <span class="text-xs text-dark-text">Enable</span>
          </label>
        </div>

        <div v-if="enableAliases">
          <p class="text-[11px] text-semi-dark-text mb-2">Map source column names (or indices) to output field names in the DataTable.</p>
          <div class="space-y-2">
            <div v-for="(alias, idx) in aliases" :key="idx" class="flex items-center gap-2">
              <input
                v-model="alias.source"
                type="text"
                placeholder="Source Header / Col#"
                class="flex-1 px-2 py-1 text-xs border border-stroke rounded bg-white text-dark-text focus:outline-none focus:ring-1 focus:ring-secondary"
                @input="onPayloadChange"
              />
              <svg class="w-4 h-4 text-semi-dark-text flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fill-rule="evenodd"
                  d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
              <input
                v-model="alias.target"
                type="text"
                placeholder="Output Field Name"
                class="flex-1 px-2 py-1 text-xs border border-stroke rounded bg-white text-dark-text focus:outline-none focus:ring-1 focus:ring-secondary"
                @input="onPayloadChange"
              />
              <button type="button" class="p-1 rounded text-alert hover:bg-alert hover:bg-opacity-10 transition-colors" title="Remove alias" @click="removeAlias(idx)">
                <svg class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1
                     1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
          <button
            type="button"
            class="mt-2 px-3 py-1 text-xs font-medium text-secondary border border-secondary rounded hover:bg-secondary hover:text-on-secondary transition-colors"
            @click="addAlias"
          >
            + Add Alias
          </button>
        </div>
      </div>
    </div>

    <!-- ─── JSON Preview (collapsible) ─── -->
    <div class="px-4 py-3 border-x border-b border-stroke rounded-b-lg">
      <button type="button" class="flex items-center gap-2 bg-transparent border-none cursor-pointer text-xs font-semibold text-semi-dark-text p-0" @click="showPreview = !showPreview">
        <svg :class="['w-4 h-4 transition-transform duration-200', showPreview ? 'rotate-90' : '']" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
        </svg>
        JSON Payload Preview
      </button>
      <pre v-if="showPreview" class="mt-2 p-3 bg-surface-dark border border-stroke rounded-md text-[11px] font-mono overflow-x-auto whitespace-pre leading-relaxed max-h-[400px] overflow-y-auto">{{
        payloadJson
      }}</pre>
    </div>

    <!-- ─── Output Variable ─── -->
    <VariableSelect label="Output Variable" v-model="outputVariable" class="mt-3 w-full" system :filter-data-type="['DataTable']" @update:modelValue="onPayloadChange" />
    <p v-if="submitted && !outputVariable" class="mt-1 text-[11px] text-alert font-medium">Output variable is required.</p>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import uuidv4 from '../../../helpers/uuid';
import EvaluateItemsConfig from './EvaluateItemsConfig';

/**
 * @component DataMapperExcelToDataTableConfig
 * @description Configuration UI for the "Excel → DataTable" nocode block.
 *
 * Lets users define how rows and columns are extracted from an uploaded Excel
 * file and mapped into a JavaScript object-array (DataTable).
 *
 * Configurable options:
 *  - Sheet selection by index or name
 *  - Header mode: read from a specific row **or** manually define header→column mappings
 *  - Data-row start (after header, or a custom row number) with optional end-row limit
 *  - Column range: all, contiguous range, or cherry-picked indices
 *  - Gap handling: skip empty rows, choose empty-cell placeholder
 *  - Optional column rename / alias mapping
 */
export default {
  name: 'DataMapperExcelToDataTableConfig',
  components: {},
  props: {
    item: { type: Object, default: null },
  },
  data() {
    let init;
    if (this.item) {
      init = JSON.parse(JSON.stringify(this.item.payload));
    } else {
      init = structuredClone(EvaluateItemsConfig['data-mapper-excel-to-datatable'].payload);
    }
    return {
      /* ── Variable bindings ── */
      inputVariable: init?.inputVariable || '',
      outputVariable: init?.outputVariable || '',

      /* ── Sheet ── */
      sheetRefType: init?.sheet?.refType || 'index',
      sheetIndex: init?.sheet?.index ?? 1,
      sheetName: init?.sheet?.name || '',

      /* ── Headers ── */
      headerMode: init?.headers?.mode || 'row', // 'row' | 'none'
      headerRowIndex: init?.headers?.rowIndex ?? 1,
      manualHeaders: init?.headers?.manual ? JSON.parse(JSON.stringify(init.headers.manual)) : [{ name: '', columnIndex: 1 }],

      /* ── Data rows ── */
      dataRowMode: init?.dataRows?.mode || 'afterHeader', // 'afterHeader' | 'custom'
      dataStartRow: init?.dataRows?.startRow ?? 2,
      hasEndRow: init?.dataRows?.endRow != null,
      dataEndRow: init?.dataRows?.endRow ?? null,

      /* ── Columns ── */
      columnMode: init?.columns?.mode || 'all', // 'all' | 'range' | 'pick'
      columnStart: init?.columns?.start ?? 1,
      columnEnd: init?.columns?.end ?? 10,
      pickedColumns: init?.columns?.picked ? [...init.columns.picked] : [1],

      /* ── Gap / empty handling ── */
      skipEmptyRows: init?.options?.skipEmptyRows ?? true,
      emptyCellValue: init?.options?.emptyCellValue || 'null',

      /* ── Aliases ── */
      enableAliases: init?.aliases?.enabled ?? false,
      aliases: init?.aliases?.mappings ? JSON.parse(JSON.stringify(init.aliases.mappings)) : [],

      /* ── UI state ── */
      submitted: false,
      showPreview: false,
    };
  },
  computed: {
    ...mapState(['projectData']),
    variables() {
      let variables = [];
      if (this.projectId && Object.keys(this.projectData).length) {
        variables = Object.keys(this.projectData?.[this.projectId]?.variables || {}).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())) || [];
      }
      return variables.map((variable) => ({
        id: uuidv4(),
        value: variable,
      }));
    },
    projectId() {
      return this.$route.params.projectId;
    },

    /**
     * Builds the full configuration payload emitted to the parent
     * and consumed by the backend runtime.
     */
    configPayload() {
      const cfg = {
        inputVariable: this.inputVariable,
        outputVariable: this.outputVariable,

        sheet: this.sheetRefType === 'index' ? { refType: 'index', index: this.sheetIndex } : { refType: 'name', name: this.sheetName },

        headers:
          this.headerMode === 'row'
            ? { mode: 'row', rowIndex: this.headerRowIndex }
            : {
                mode: 'none',
                manual: this.manualHeaders
                  .filter((h) => h.name)
                  .map((h) => ({
                    name: h.name,
                    columnIndex: h.columnIndex,
                  })),
              },

        dataRows: {
          mode: this.dataRowMode,
          startRow: this.dataRowMode === 'afterHeader' ? (this.headerMode === 'row' ? this.headerRowIndex : 0) + 1 : this.dataStartRow,
          ...(this.hasEndRow && this.dataEndRow ? { endRow: this.dataEndRow } : {}),
        },

        columns: this.buildColumnsConfig(),

        options: {
          skipEmptyRows: this.skipEmptyRows,
          emptyCellValue: this.emptyCellValue,
        },

        aliases: {
          enabled: this.enableAliases,
          mappings: this.enableAliases ? this.aliases.filter((a) => a.source && a.target) : [],
        },
      };
      return cfg;
    },

    payload() {
      return {
        id: this.item.id,
        type: this.item.type,
        payload: this.configPayload,
      };
    },
    payloadJson() {
      return JSON.stringify(this.payload, null, 2);
    },
  },
  methods: {
    ...mapActions(['showToastMessage']),

    buildColumnsConfig() {
      if (this.columnMode === 'range') {
        return {
          mode: 'range',
          start: this.columnStart,
          end: this.columnEnd,
        };
      }
      if (this.columnMode === 'pick') {
        return {
          mode: 'pick',
          picked: this.pickedColumns.filter((c) => c >= 1),
        };
      }
      return { mode: 'all' };
    },

    /* ── Manual headers ── */
    addManualHeader() {
      this.manualHeaders.push({ name: '', columnIndex: this.manualHeaders.length + 1 });
      this.onPayloadChange();
    },
    removeManualHeader(idx) {
      this.manualHeaders.splice(idx, 1);
      this.onPayloadChange();
    },

    /* ── Picked columns ── */
    addPickedColumn() {
      const next = this.pickedColumns.length > 0 ? Math.max(...this.pickedColumns) + 1 : 1;
      this.pickedColumns.push(next);
      this.onPayloadChange();
    },
    removePickedColumn(idx) {
      this.pickedColumns.splice(idx, 1);
      this.onPayloadChange();
    },

    /* ── Aliases ── */
    addAlias() {
      this.aliases.push({ source: '', target: '' });
      this.onPayloadChange();
    },
    removeAlias(idx) {
      this.aliases.splice(idx, 1);
      this.onPayloadChange();
    },

    /* ── Emit ── */
    onPayloadChange() {
      this.$nextTick(() => {
        this.submitted = true;
        this.$emit('update', this.payload);
      });
    },
  },
};
</script>

<style scoped>
/* Minimal custom styles — accent-color fallback for older browsers */
input[type='radio'],
input[type='checkbox'] {
  width: 14px;
  height: 14px;
}
</style>
